import { useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { WS_EVENTS, type WsMessageDeletedPayload } from '@twinden/shared';
import { currentFamilyIdAtom, currentUserAtom } from '../store';
import { api } from '../lib/api';
import { onMessageSent } from '../lib/outbox';
import {
  backfill,
  deleteIndex,
  forgetExpired,
  forgetProgress,
  indexMessages,
  openIndexFor,
  reindexMessage,
  type IndexableMessage,
} from '../lib/searchIndex';
import { useWebSocket } from './useWebSocket';
import { KEYS_ARRIVED_EVENT } from './useE2EE';

// Keeps the on-device search index (M25, D41) current: new messages as they
// arrive, your own as they're sent, edits and deletes, and a background
// backfill of the selected family's chats. Mounted once, in the app shell.
export function useSearchIndexSync() {
  const userId = useAtomValue(currentUserAtom)?.id ?? null;
  const familyId = useAtomValue(currentFamilyIdAtom);
  const { onEvent, connectionEpoch } = useWebSocket();
  const previousUser = useRef<string | null>(null);

  // Signing out deletes the previous user's index from this device (D44).
  useEffect(() => {
    if (previousUser.current && previousUser.current !== userId) {
      deleteIndex(previousUser.current);
    }
    previousUser.current = userId;
    openIndexFor(userId);
    // Disappearing messages whose time came while the app was closed.
    if (userId) void forgetExpired();
  }, [userId]);

  useEffect(
    () =>
      onEvent((message) => {
        if (
          message.event === WS_EVENTS.MESSAGE_RECEIVED ||
          // Yours, sent from another of your devices (D206).
          message.event === WS_EVENTS.MESSAGE_SENT
        ) {
          const payload = message.payload as IndexableMessage & {
            familyId?: string | null;
          };
          const family = payload.familyId ?? familyId;
          if (family) void indexMessages([payload], family);
        } else if (message.event === WS_EVENTS.MESSAGE_UPDATED) {
          void reindexMessage(message.payload as IndexableMessage);
        } else if (message.event === WS_EVENTS.MESSAGE_DELETED) {
          // Disappeared (D238): gone from this device's index too.
          void forgetExpired(
            (message.payload as WsMessageDeletedPayload).messageIds
          );
        }
      }),
    [onEvent, familyId]
  );

  // Your own messages (the server doesn't echo them back to you).
  useEffect(
    () =>
      onMessageSent((message) => {
        if (familyId)
          void indexMessages([message as IndexableMessage], familyId);
      }),
    [familyId]
  );

  // Locked messages couldn't be indexed; once their key arrives, go through
  // that chat's history again (M28).
  const [rescan, setRescan] = useState(0);
  useEffect(() => {
    const onKeys = (event: Event) => {
      const { conversationId } = (
        event as CustomEvent<{ conversationId?: string }>
      ).detail;
      if (!conversationId) return;
      void forgetProgress(conversationId).then(() => setRescan((n) => n + 1));
    };
    window.addEventListener(KEYS_ARRIVED_EVENT, onKeys);
    return () => window.removeEventListener(KEYS_ARRIVED_EVENT, onKeys);
  }, []);

  // Catch up and fill in history; again after every reconnect.
  useEffect(() => {
    if (!userId || !familyId) return;
    const controller = new AbortController();
    api
      .getConversations(familyId)
      .then((response) =>
        backfill(
          familyId,
          response.data.map((conversation) => conversation.id),
          controller.signal
        )
      )
      .catch(() => undefined);
    return () => controller.abort();
  }, [userId, familyId, connectionEpoch, rescan]);
}
